/**
 * The ONE wire contract for "what Clay's run did", as it lands in
 * `launcher_dispatch_log.result_status` / `.result_data`.
 *
 * # Several writers, one shape
 *
 * Terra learns a Clay run's outcome by several routes, and the Dispatch
 * inspector must render them identically:
 *
 *   1. **Reading the routine run (the permanent fallback, prod AND local).**
 *      A Playbooks dispatch starts its Clay workflow through the Clay Public
 *      API as a ROUTINE RUN — the same transport `generate_message_workflow`
 *      already uses — and then reads the run's terminal output back.
 *      Request/response, so it works identically everywhere, including a
 *      laptop Clay cannot reach. `launcher.clay-run-status` owns that read
 *      (and the Playbooks dispatch-log re-check endpoint re-asks with the
 *      same seam); the projection that turns the routine's output into this
 *      shape lives in exactly one place
 *      (`src/services/clay/routine-result-projection.ts`) and its output is
 *      pinned against this schema by that module's own unit test.
 *   1b. **Clay's platform run-completion webhook (the fast path).** A start
 *      that carried `webhook_id` makes Clay POST
 *      `{ data: { routine_run_id } }` to `POST /api/flow/clay-callbacks` the
 *      moment the run finishes; `src/services/launcher/dispatch-run-settle.ts`
 *      then does ONE of the reads above and merges the SAME projection,
 *      marked `clay_result_source: 'callback'`. It is an accelerator — Clay
 *      does not guarantee delivery, so (1) is never removed.
 *   2. **The in-workflow push callback.** The Clay workflow can also POST to the
 *      launcher's unauthenticated Clay-callback route
 *      (`POST /api/tools/launcher/dispatch-result`), which merges the posted
 *      `result_data` into the jsonb column with `||`. That handler stays
 *      deliberately permissive about EXTRA keys (it has merged arbitrary
 *      objects since before this file existed, and narrowing it would silently
 *      drop fields Clay already sends) — this schema documents the keys it is
 *      EXPECTED to carry, and is what the reader parses.
 *
 * # Read posture
 *
 * `result_data` is a MERGED blob: it also carries `webhook_response` (the
 * dispatch workflow's synchronous echo) and whatever loose fields older
 * `/dispatch-result` callbacks posted (`final_subject`, …). So the reader
 * parses with plain `z.object` (unknown keys stripped, not rejected) and a
 * `safeParse` — a blob with no `clay_run` is simply "no Clay run info yet",
 * never an error. Never `.strict()` here: strict would reject every row that
 * also carries a webhook echo.
 *
 * Types are `z.infer<>` only — never a hand-written parallel interface
 * (`terra/no-parallel-schema-and-type`, `terra/no-duplicate-schema-name`).
 */
import { z } from 'zod';

// `result_status` vocabulary

/**
 * The `clay_*` values this feature writes to `launcher_dispatch_log.result_status`.
 *
 * DELIBERATELY DISJOINT from the `result` column's vocabulary
 * (`queued`/`processing`/`sent`/`failed`). `result` still means "did Terra get
 * the webhook POST accepted" — `sent` keeps that meaning exactly — and
 * `result_status` is the independent "what did Clay then do with it" axis. A
 * row can be `result = 'sent'` and `result_status = 'clay_blocked'` at the
 * same time, and that combination is the whole point: the POST worked, the
 * send did not.
 */
export const CLAY_RUN_RESULT_STATUSES = [
  /** Clay has the run queued but no step has started. */
  'clay_pending',
  /** Running, waiting, or paused — anything still in motion. */
  'clay_running',
  /** The run finished and did not hit a block node. */
  'clay_completed',
  /** The run stopped itself on purpose (no sender, unsafe delivery, …). */
  'clay_blocked',
  /** Clay reports the run errored. */
  'clay_failed',
  /**
   * The run was started but never produced a terminal result before the poll
   * budget ran out (or Clay stopped acknowledging it). Distinct from a NULL
   * `result_status`, which means "nothing has looked yet".
   */
  'clay_not_found',
] as const;

export const ClayRunResultStatusSchema = z.enum(CLAY_RUN_RESULT_STATUSES);
export type ClayRunResultStatus = z.infer<typeof ClayRunResultStatusSchema>;

/**
 * Statuses that will never change again, so a poller stops and the inspector
 * stops refreshing. `clay_pending`/`clay_running` are the only live ones.
 */
export const TERMINAL_CLAY_RUN_RESULT_STATUSES: readonly ClayRunResultStatus[] = [
  'clay_completed',
  'clay_blocked',
  'clay_failed',
  'clay_not_found',
];

/**
 * The terminal statuses a LATE result may still overwrite (plan
 * 2026-09-30-003, U2).
 *
 * "Terminal" above means "stop POLLING" — nothing is watching the row any
 * more. It does NOT mean "Clay's outcome is known", and for exactly one status
 * those two come apart: `clay_not_found` is the poll's GIVE-UP write ("~20
 * minutes passed and Clay never produced a terminal result"), not an answer.
 * Clay may well have delivered, and its run-completion callback can arrive
 * afterwards.
 *
 * Without this set, `src/services/launcher/dispatch-run-settle.ts` answered
 * such a callback `already_terminal` — no Clay read, nothing written — and a
 * DELIVERED run stayed unresolved until someone opened the play and triggered
 * a manual re-check.
 *
 * `mini-apps/playbooks/dispatch-recheck-eligibility.ts#isRecheckEligible`
 * already treats `clay_not_found` as always re-checkable, at any age, for
 * exactly this reason; this constant is that same judgement, stated where core
 * can see it. That module stays zod-free and unchanged on purpose — the
 * browser bundle reads it, so it imports nothing but a timing number, and core
 * cannot import a mini-app — so the agreement between the two is pinned by
 * test rather than by a shared import.
 *
 * Overwriting is SAFE under U1's run fence: the give-up write is status-only,
 * so the row still carries the same `routine_run_id` the callback names and
 * the fence passes. A row a rep has since RETRIED has had its `clay_*` keys
 * cleared, so the callback's SELECT finds no row at all (`no_row`) and the
 * newer attempt is untouched either way.
 */
export const OVERWRITABLE_CLAY_RUN_RESULT_STATUSES: readonly ClayRunResultStatus[] = [
  'clay_not_found',
];

// `result_data` — the `clay_*` keys

/** One rendered sequence step, as Clay's `Render copy` / test-preview nodes emit it. */
export const ClayRunPreviewStepSchema = z.object({
  subject: z.string(),
  body: z.string(),
});

/**
 * What Clay actually rendered, so a rep can diff it against Terra's payload.
 * `html_body` is whichever of Clay's `html_body` / `body` spellings was
 * present (the two nodes disagree); every string is length-capped before
 * storage (see `CLAY_PREVIEW_MAX_CHARS`).
 */
export const ClayRunPreviewSchema = z.object({
  subject: z.string(),
  html_body: z.string(),
  steps: z.array(ClayRunPreviewStepSchema),
});

/** Why the run refused to deliver, and which node refused. */
export const ClayRunBlockSchema = z.object({
  reason: z.string(),
  node: z.string(),
});

/** Who Clay resolved as the sender (its answer, not Terra's request). */
export const ClayRunSenderSchema = z.object({
  sender_email: z.string().nullable(),
  sender_first_name: z.string().nullable(),
  sender_source: z.string().nullable(),
});

/**
 * The run itself.
 *
 * `status` is Clay's OWN word kept verbatim — the terminal node's `outcome`
 * (`test_preview`, `blocked`, `delivered`, …) or the lifecycle word
 * (`pending`, `failed`) — deliberately NOT narrowed to an enum, so a
 * vocabulary Clay adds tomorrow stays visible in the UI instead of failing
 * the parse. The narrowed judgement is `result_status`.
 *
 * `routine_run_id` is nullable because the failure case includes "the run
 * could not be started at all", which has no id to record.
 */
export const ClayRunDescriptorSchema = z.object({
  /** Clay Public API `routine_run_id` — the handle for `GET /routines/run/{id}/results`. */
  routine_run_id: z.string().nullable(),
  /**
   * The Clay WORKFLOW run id (`wfr_…`), present when the run was found by
   * `launcher.dispatch`'s reconcile (a START that timed out, but Clay's runs
   * query shows it landed) or repaired by a Playbooks retry's Clay check. Such
   * a run has no `routine_run_id` — the START's answer never arrived — and
   * `status` is `'reconciled'`. Optional: every other writer omits it.
   */
  workflow_run_id: z.string().nullable().optional(),
  status: z.string(),
  progress_percentage: z.number().nullable(),
  /** The node that produced the outcome — "Return test preview", "Stop unsafe delivery", … */
  current_stage: z.string().nullable(),
  /** ISO timestamp of the observation, not of the run. */
  updated_at: z.string().nullable(),
});

// Final-status v2 (plan 2026-09-27-003) — the Terra ↔ V3 contract additions.
//
// V3's `Return final status` node gains `checks`, `steps: [{subject, body}]`
// and `contract_version: 2`. EVERY new key is optional here and every reader
// `safeParse`s: Terra deploys first and must render identically against
// today's V3 output, which has none of them. No fake defaults — an absent
// check is absent, not "pass".

/** One step of V3's own checklist. `stop` = Clay decided not to send (on purpose); `error` = something broke. */
export const ClayCheckStateSchema = z.enum(['pass', 'stop', 'skip', 'error']);
export const ClayCheckSchema = z.object({ state: ClayCheckStateSchema, reason: z.string() });

/** V3's "What Clay did" block, projected into `result_data.clay_checks`. */
export const ClayRunChecksSchema = z.object({
  email_validation: ClayCheckSchema.optional(),
  sender: ClayCheckSchema.optional(),
  copy_qa: ClayCheckSchema.optional(),
  delivery: ClayCheckSchema.optional(),
});

/**
 * The copy Clay finally sent (or, on a deliberate non-send, had prepared),
 * one entry per ordinal step. Its OWN key with its own size budget
 * (`routine-result-projection.ts#CLAY_FINAL_COPY_MAX_TOTAL_CHARS`), and
 * EXCLUDED from the send-history list read — only the per-row sent-copy
 * endpoint reads it.
 */
/**
 * One step of {@link ClayFinalCopySchema}. V3 echoes the step's Gong type
 * (`SEND_EMAIL`, `CALL`, …) as `action_type` so Terra can tell a blank Gong-run
 * CALL slot from an email reply without leaning on its own stored payload
 * (PR #1715 review, finding 3). Optional: older V3 output has none, and `''`
 * counts as unknown. `clay_preview.steps` stays on the plain step shape.
 */
export const ClayFinalCopyStepSchema = ClayRunPreviewStepSchema.extend({ action_type: z.string().optional() });

export const ClayFinalCopySchema = z.object({ steps: z.array(ClayFinalCopyStepSchema) });

/**
 * Final-status `status` values that mean "Clay decided, on purpose, not to
 * deliver" — as opposed to an error, or a genuine completion (a send, or a
 * test preview). Lives here (not only in the projection service) because the
 * browser-side rep-status classifier needs the same list; the service
 * re-exports it.
 */
export const CLAY_FINAL_STATUS_NON_SEND = [
  'Skipped',
  'Suppressed',
  'Already in Flow',
  'Invalid Email',
] as const;

/**
 * The `clay_run.status` Terra writes (with `result_status: 'clay_completed'`)
 * when Clay says a run FINISHED but its outcome can no longer be read — the
 * routine result has aged out (404) and the workflow runs query only says
 * `completed` (PR #1715 review, finding 2). Terra won't guess what the run
 * delivered, so the rep sees "Handed to Clay · delivery not tracked for this
 * send" (`dispatch-rep-status.ts`), and the row stops being re-checked
 * instead of looking stuck forever. Written by
 * `routine-result-projection.ts#clayRoutineFinishedUntrackedProjection`;
 * never a word Clay itself returns.
 */
export const CLAY_RUN_STATUS_FINISHED_UNTRACKED = 'Finished — outcome not recorded' as const;

/**
 * The ONE rep-facing status a Dispatch log row shows
 * (`mini-apps/playbooks/dispatch-rep-status.ts` is the only classifier).
 */
export const RepDispatchStatusSchema = z.enum([
  'delivered',
  'in_progress',
  'not_sent',
  'failed_to_start',
  'crashed',
]);

/**
 * The `clay_*` slice of `launcher_dispatch_log.result_data`. Plain `z.object`
 * (unknown keys stripped) on purpose — see the "Read posture" note above.
 *
 * `clay_alert_posted` is V3's own `alert_posted` (it already told ops);
 * `clay_crash_alerted` is Terra's (plan 2026-09-27-003, U7 — Terra posted the
 * crash alert itself). Both are only ever `true` or absent.
 */
export const ClayRunResultDataSchema = z.object({
  clay_run: ClayRunDescriptorSchema,
  clay_preview: ClayRunPreviewSchema.optional(),
  clay_block: ClayRunBlockSchema.optional(),
  clay_sender: ClayRunSenderSchema.optional(),
  clay_leftover_tokens: z.array(z.string()).optional(),
  clay_checks: ClayRunChecksSchema.optional(),
  clay_final_copy: ClayFinalCopySchema.optional(),
  clay_alert_posted: z.boolean().optional(),
  clay_crash_alerted: z.boolean().optional(),
  /**
   * WHICH transport learned this row's outcome (plan 2026-09-30-002).
   * `'callback'` — Clay's platform run-completion webhook reached
   * `POST /api/flow/clay-callbacks` and `dispatch-run-settle.ts` merged it.
   * `'poll'` — `launcher.clay-run-status` read the run itself, on a dispatch
   * that DID ask Clay to call back (so the callback is known to have been
   * requested and not arrived; a run started with no callback configured
   * writes nothing here rather than a misleading `'poll'`).
   *
   * Optional and absent on every row written before this existed. Not
   * rep-facing: it is the measurable marker that says whether the push path
   * is carrying real traffic, which is what gates ever making the poll
   * sparser.
   */
  clay_result_source: z.enum(['callback', 'poll']).optional(),
});

export type ClayRunPreviewStep = z.infer<typeof ClayRunPreviewStepSchema>;
export type ClayRunPreview = z.infer<typeof ClayRunPreviewSchema>;
export type ClayRunBlock = z.infer<typeof ClayRunBlockSchema>;
export type ClayRunSender = z.infer<typeof ClayRunSenderSchema>;
export type ClayRunDescriptor = z.infer<typeof ClayRunDescriptorSchema>;
export type ClayRunResultData = z.infer<typeof ClayRunResultDataSchema>;
export type ClayCheckState = z.infer<typeof ClayCheckStateSchema>;
export type ClayCheck = z.infer<typeof ClayCheckSchema>;
export type ClayRunChecks = z.infer<typeof ClayRunChecksSchema>;
export type ClayFinalCopy = z.infer<typeof ClayFinalCopySchema>;
export type ClayFinalCopyStep = z.infer<typeof ClayFinalCopyStepSchema>;
export type RepDispatchStatus = z.infer<typeof RepDispatchStatusSchema>;

/**
 * Read the `clay_*` slice out of a merged `result_data` blob. `null` means
 * "this row has no Clay run info" — including the common cases of `null`
 * `result_data`, a blob carrying only `webhook_response`, and a blob whose
 * `clay_run` drifted into a shape we can't read. Never throws: this is a
 * display path over a jsonb column (CLAUDE.md, db-json parsing).
 */
export function readClayRunResultData(resultData: unknown): ClayRunResultData | null {
  const parsed = ClayRunResultDataSchema.safeParse(resultData);
  return parsed.success ? parsed.data : null;
}

// `result_data` — `launcher.dispatch`'s own bookkeeping keys (plan 2026-09-26-003)

/**
 * `result_data.dispatch_failure` — written by `launcher.dispatch` on EVERY
 * terminal failure of a Clay routine START, and read by the Playbooks
 * inspector (the row headline) and the retry endpoint.
 *
 *   reason     — the transport's failure class (`no_response`, `http`, `drift`,
 *                `pool`, `unconfigured`, `invalid_item_id`, `unknown`).
 *   ambiguous  — might Clay have had a run anyway (a timeout, a 5xx…)?
 *   reconciled — what Clay's runs query said when asked: `none` (no run for
 *                this member while other runs of the send exist — safe to
 *                retry), `no_sibling_runs` (no run of the send at all, so
 *                Clay may not show it yet — NOT safe to retry), `unavailable`
 *                (couldn't tell), or `null` (never asked: the failure was
 *                not ambiguous).
 *   attempts   — how many STARTs were made.
 *   repaired_by — set when the row was later repaired to `sent`: `callback`
 *                (Clay's dispatch-result callback arrived) or `retry_check`
 *                (a retry asked Clay and found the run).
 */
export const DispatchFailureSchema = z.object({
  reason: z.string(),
  ambiguous: z.boolean(),
  reconciled: z.enum(['none', 'unavailable', 'no_sibling_runs']).nullable(),
  attempts: z.number().int().nonnegative(),
  repaired_by: z.enum(['callback', 'retry_check']).optional(),
});
export type DispatchFailure = z.infer<typeof DispatchFailureSchema>;

/**
 * `result_data.dispatch_reconcile` — a marker that is present ONLY while
 * `launcher.dispatch` is asking Clay whether an ambiguous START landed, and is
 * removed when the verdict lands (found, none, or couldn't tell). Written once
 * per reconcile, at its start; `attempt` is which START is being checked.
 * Plain `z.object` (unknown keys stripped) so a row an older build wrote with
 * `tick`/`checked_at` still reads as the marker.
 */
export const DispatchReconcileSchema = z.object({
  attempt: z.number().int().positive(),
});
export type DispatchReconcile = z.infer<typeof DispatchReconcileSchema>;

const DispatchFailureSliceSchema = z.object({ dispatch_failure: DispatchFailureSchema });
const DispatchReconcileSliceSchema = z.object({ dispatch_reconcile: DispatchReconcileSchema });

/** `result_data.dispatch_failure`, or `null` (absent or drifted). Never throws — a display path. */
export function readDispatchFailure(resultData: unknown): DispatchFailure | null {
  const parsed = DispatchFailureSliceSchema.safeParse(resultData);
  return parsed.success ? parsed.data.dispatch_failure : null;
}

/** `result_data.dispatch_reconcile`, or `null`. Never throws. */
export function readDispatchReconcile(resultData: unknown): DispatchReconcile | null {
  const parsed = DispatchReconcileSliceSchema.safeParse(resultData);
  return parsed.success ? parsed.data.dispatch_reconcile : null;
}
